import React, { Suspense, useContext } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import MainLayout from '@components/layout/MainLayout';

import { HubSpot } from '@/core/Hubspot';
import {
  PermissionsRoute,
  useHasPermissions,
  useUserData,
  PermissionsContext,
  useHasVisibleSettings,
  useClientSettings
} from '@/shared/access';
import { PartnerRoute, RedirectWithQueries } from '@/shared/navigation';

const LocationDetailsPageNew = withSuspense(
  React.lazy(() => import('@pages/LocationDetailsPage').then(module => ({ default: module.LocationDetailsPage })))
);

const PalletsPage = withSuspense(
  React.lazy(() => import('./view/pages/pallets').then(module => ({ default: module.PalletsPage })))
);

const ProductCosts = withSuspense(
  React.lazy(() => import('./view/pages/ProductCosts').then(module => ({ default: module.ProductCosts })))
);
const Plants = withSuspense(React.lazy(() => import('./view/pages/Plants').then(module => ({ default: module.Plants }))));
const Messages = withSuspense(React.lazy(() => import('./view/pages/Messages').then(module => ({ default: module.Messages }))));
const Dashboards = withSuspense(React.lazy(() => import('@pages/Dashboards').then(module => ({ default: module.Dashboards }))));
const ActivityPage = withSuspense(React.lazy(() => import('./view/pages/ActivityPage')));
const InventorySettings = withSuspense(React.lazy(() => import('./view/pages/Inventory/Settings')));
const InventoryTransactions = withSuspense(
  React.lazy(() => import('./view/pages/Inventory/Transactions').then(module => ({ default: module.Transactions })))
);

const InventoryDashboard = withSuspense(
  React.lazy(() => import('./view/pages/Inventory/Dashboard').then(module => ({ default: module.Dashboard })))
);
const BlockOffAvailability = withSuspense(React.lazy(() => import('./view/pages/BlockOffAvailability')));
const DriversPage = withSuspense(
  React.lazy(() => import('./view/pages/drivers/DriversPage').then(module => ({ default: module.DriversPage })))
);
const ContractorInfo = withSuspense(
  React.lazy(() => import('./view/pages/ContractorInfo').then(module => ({ default: module.ContractorInfo })))
);
const FinanceAccountPage = withSuspense(React.lazy(() => import('./view/pages/FinancePage/FinanceAccountPage')));
const FinancePage = withSuspense(
  React.lazy(() => import('./view/pages/FinancePage').then(module => ({ default: module.FinancePage })))
);

const NewWidgetPage = withSuspense(React.lazy(() => import('./view/pages/Widgets/NewWidgetPage')));
const OrderInfoPage = withSuspense(
  React.lazy(() => import('./view/pages/OrderInfoPage').then(module => ({ default: module.OrderInfoPage })))
);
const ProductsPage = withSuspense(React.lazy(() => import('./view/pages/ProductsPage')));
const Products = withSuspense(React.lazy(() => import('./view/pages/Products').then(module => ({ default: module.Products }))));
const Profile = withSuspense(React.lazy(() => import('./view/pages/Profile').then(module => ({ default: module.Profile }))));
const ProjectsPage = withSuspense(React.lazy(() => import('./view/pages/ProjectsPage')));
const Projects = withSuspense(
  React.lazy(() => import('./view/pages/PrejectsRedesign/Projects').then(module => ({ default: module.Projects })))
);
const ProjectsDetails = withSuspense(
  React.lazy(() => import('./view/pages/PrejectsRedesign/ProjectDetails').then(module => ({ default: module.ProjectDetails })))
);
const RequestsPage = withSuspense(React.lazy(() => import('./view/pages/RequestsPage')));
const StatisticsPage = withSuspense(
  React.lazy(() => import('./view/pages/StatisticsPage').then(module => ({ default: module.StatisticsPage })))
);
const SupplierInfo = withSuspense(
  React.lazy(() => import('./view/pages/SupplierInfo').then(module => ({ default: module.SupplierInfo })))
);
const Suppliers = withSuspense(
  React.lazy(() => import('./view/pages/Suppliers').then(module => ({ default: module.Suppliers })))
);
const TaxPage = withSuspense(React.lazy(() => import('./view/pages/TaxPage').then(module => ({ default: module.TaxPage }))));
const TermsAndConditions = withSuspense(
  React.lazy(() => import('./view/pages/TermsAndConditions').then(module => ({ default: module.TermsAndConditions })))
);
const WidgetsPage = withSuspense(React.lazy(() => import('./view/pages/Widgets/WidgetsPage')));
const ContractorsPage = withSuspense(
  React.lazy(() => import('./view/pages/ContractorsPage').then(module => ({ default: module.ContractorsPage })))
);
const OrderTransactionsScreen = withSuspense(React.lazy(() => import('./view/screens/OrderTransactionsScreen')));
import { useDevTools } from '@/shared/devTools';
const NewProjectScreen = withSuspense(React.lazy(() => import('./view/pages/Projects/NewProjectScreen')));
const TrucksPage = withSuspense(React.lazy(() => import('@pages/TrucksPage').then(module => ({ default: module.TrucksPage }))));
const SchedulePage = withSuspense(
  React.lazy(() => import('@pages/schedule/SchedulePage').then(module => ({ default: module.SchedulePage })))
);
const OrderCreatePage = withSuspense(
  React.lazy(() => import('@pages/OrderCreatePage').then(module => ({ default: module.OrderCreatePage })))
);
const DeliveriesPage = withSuspense(
  React.lazy(() => import('@pages/DeliveriesPage').then(module => ({ default: module.DeliveriesPage })))
);

function withSuspense(Component: React.ComponentType & any) {
  return function WithSuspense(props: any) {
    return (
      <Suspense fallback={null}>
        <Component {...props} />
      </Suspense>
    );
  };
}

export default function AdminRouter() {
  const user = useUserData();
  const canShowTruck = useHasVisibleSettings('trucks');
  const canShowNewProducts = useClientSettings()?.products?.new_ui;
  const devTools = useDevTools();
  if (!user) {
    return null;
  }

  return (
    <MainLayout>
      <Switch>
        <PermissionsRoute
          permissions={['permissions.orders.show']}
          redirectTo="/"
          path="/(p-)?:partnerId?/orders"
          exact
          component={Dashboards}
          key="/orders"
        />
        <PermissionsRoute
          permissions={['permissions.orders.show', 'permissions.orders.create']}
          redirectTo="/"
          path="/(p-)?:partnerId?/orders/new/:orderId?"
          component={OrderCreatePage}
          key="/orders/new"
        />
        <PermissionsRoute
          permissions={['permissions.orders.show', 'permissions.orders.create']}
          redirectTo="/"
          path="/(p-)?:partnerId?/orders/edit/:orderId?"
          component={() => <OrderCreatePage edit />}
          key="/orders/edit"
        />
        <PermissionsRoute
          permissions={['permissions.orders.show']}
          redirectTo="/"
          path="/(p-)?:partnerId?/orders/:orderId"
          exact
          component={OrderInfoPage}
          key="/orders/:orderId"
        />
        ,
        <PermissionsRoute
          permissions={['permissions.orders.show']}
          redirectTo="/"
          path="/(p-)?:partnerId?/orders/:orderId/transactions"
          exact
          component={OrderTransactionsScreen}
          key="/orders/:orderId/transactions"
        />
        <PermissionsRoute
          permissions="permissions.statistic.show"
          redirectTo="/"
          path="/(p-)?:partnerId?/statistics"
          exact
          component={StatisticsPage}
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/deliveries"
          exact
          component={DeliveriesPage}
          permissions={['permissions.driver_tasks.show', 'settings.driver_tasks.visible']}
          visibilitySettings="driver_tasks"
          redirectTo="/"
        />
        <PermissionsRoute
          permissions="permissions.locations.show"
          visibilitySettings="locations"
          redirectTo="/"
          path="/(p-)?:partnerId?/locations-new/:id/:section/:category?"
          component={LocationDetailsPageNew}
          key="/locations-new/:id/:section/:category?"
        />
        <PermissionsRoute
          permissions="permissions.locations.show"
          visibilitySettings="locations"
          redirectTo="/"
          path="/(p-)?:partnerId?/locations"
          component={Plants}
          key="/locations"
        />
        {/*<PartnerRoute path="/forgot-password" exact component={ForgotPasswordScreen} />*/}
        <PermissionsRoute
          permissions="permissions.products.show"
          visibilitySettings="products"
          redirectTo="/"
          path="/(p-)?:partnerId?/products/:section?/:category?"
          exact
          component={canShowNewProducts ? Products : ProductsPage}
        />
        <PermissionsRoute
          permissions="permissions.products.cost_prices.show"
          // @ts-ignore wrong ts declaration
          visibilitySettings="products.cost_prices"
          redirectTo="/"
          path="/(p-)?:partnerId?/product-costs/:category?"
          exact
          component={ProductCosts}
        />
        <PermissionsRoute
          permissions="permissions.suppliers.show"
          visibilitySettings="suppliers"
          redirectTo="/"
          path="/(p-)?:partnerId?/suppliers"
          exact
          component={Suppliers}
          key="/suppliers"
        />
        <PermissionsRoute
          permissions="permissions.suppliers.show"
          visibilitySettings="suppliers"
          redirectTo="/"
          path="/(p-)?:partnerId?/suppliers/:id"
          exact
          component={SupplierInfo}
          key="/suppliers/:id"
        />
        <PermissionsRoute
          permissions="permissions.contractors.show"
          visibilitySettings="contractors"
          redirectTo="/"
          path="/(p-)?:partnerId?/contractors"
          exact
          component={ContractorsPage}
          key="/contractors"
        />
        <PermissionsRoute
          permissions="permissions.contractors.show"
          visibilitySettings="contractors"
          redirectTo="/"
          path="/(p-)?:partnerId?/contractors/:id"
          exact
          component={ContractorInfo}
          key="/contractors/:id"
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/operators"
          exact
          component={DriversPage}
          permissions="permissions.drivers.show"
          visibilitySettings="drivers"
          redirectTo="/"
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/inventory/settings/:settingType?"
          exact
          visibilitySettings="inventory"
          component={InventorySettings}
          permissions="permissions.inventory.show"
          redirectTo="/"
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/inventory/transactions/:type?"
          exact
          visibilitySettings="inventory"
          component={InventoryTransactions}
          permissions="permissions.inventory.show"
          redirectTo="/"
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/inventory/dashboard"
          exact
          visibilitySettings="inventory"
          component={InventoryDashboard}
          permissions="permissions.inventory.show"
          redirectTo="/"
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/schedule"
          exact
          visibilitySettings="scheduling"
          component={() => <RedirectWithQueries to="/schedule/operators" />}
          permissions={['permissions.scheduling.show']}
          redirectTo="/orders"
          key="/schedule_root"
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/schedule/:section"
          exact
          component={SchedulePage}
          permissions={['permissions.scheduling.show']}
          redirectTo="/"
          key="/schedule"
        />
        <PermissionsRoute
          permissions="permissions.activity.show"
          redirectTo="/"
          path="/activity"
          exact
          component={ActivityPage}
        />
        <PermissionsRoute
          permissions="permissions.messages.show"
          visibilitySettings="messages"
          redirectTo="/"
          path="/(p-)?:partnerId?/messages"
          component={Messages}
          key="/messages"
        />
        <PermissionsRoute
          permissions="permissions.messages.show"
          visibilitySettings="messages"
          redirectTo="/"
          path="/(p-)?:partnerId?/messages/:id"
          component={Messages}
          key="/messages/:id"
        />
        <PermissionsRoute
          permissions={'permissions.finances.show'}
          visibilitySettings="finances"
          path="/(p-)?:partnerId?/finances/:section"
          exact
          redirectTo="/"
          component={FinancePage}
          key="/finance"
        />
        ,
        <PermissionsRoute
          permissions={'permissions.finances.show'}
          visibilitySettings="finances"
          redirectTo="/"
          path="/(p-)?:partnerId?/finance/account"
          exact
          component={FinanceAccountPage}
          key="/finance/account"
        />
        <PermissionsRoute
          permissions="permissions.projects.show"
          visibilitySettings="projects"
          path="/(p-)?:partnerId?/projects"
          exact
          redirectTo="/"
          component={devTools?.oldProjectsScreen ? ProjectsPage : Projects}
          key="/projects"
        />
        ,
        <PermissionsRoute
          permissions="permissions.projects.show"
          visibilitySettings="projects"
          redirectTo="/"
          path="/(p-)?:partnerId?/projects/:id/:section?"
          key="/projects/:id"
          component={devTools?.oldProjectsScreen ? NewProjectScreen : ProjectsDetails}
        />
        <PermissionsRoute
          visibilitySettings="widgets"
          redirectTo="/"
          permissions="permissions.widgets.show"
          path="/(p-)?:partnerId?/widgets"
          exact
          component={WidgetsPage}
          key="/widgets"
        />
        <PermissionsRoute
          visibilitySettings="widgets"
          redirectTo="/"
          permissions="permissions.widgets.show"
          path="/(p-)?:partnerId?/widgets/new"
          component={NewWidgetPage}
          key="/widgets/new"
        />
        <PermissionsRoute
          visibilitySettings="widgets"
          redirectTo="/"
          permissions="permissions.widgets.show"
          path="/(p-)?:partnerId?/widgets/:id"
          component={NewWidgetPage}
          key="/widgets/:id"
        />
        <PermissionsRoute
          redirectTo="/"
          visibilitySettings="taxes"
          permissions="permissions.taxes.show"
          path="/(p-)?:partnerId?/tax"
          exact
          component={TaxPage}
          key="/tax"
        />
        {canShowTruck && <PartnerRoute path="/(p-)?:partnerId?/trucks" exact component={TrucksPage} key="/trucks" />}
        <PermissionsRoute
          permissions="permissions.profile.show"
          redirectTo="/"
          path="/(p-)?:partnerId?/profile/:tab?"
          component={Profile}
        />
        <PermissionsRoute
          permissions="permissions.block_off_availability.show"
          visibilitySettings="block_off_availability"
          redirectTo="/"
          path="/(p-)?:partnerId?/availability/block-off"
          exact
          component={BlockOffAvailability}
        />
        <PermissionsRoute
          visibilitySettings="requests"
          permissions="permissions.requests.show"
          redirectTo="/"
          path="/(p-)?:partnerId?/requests"
          exact
          component={RequestsPage}
        />
        <PermissionsRoute
          visibilitySettings="requests"
          permissions="permissions.requests.show"
          redirectTo="/"
          path="/(p-)?:partnerId?/requests/:section"
          component={RequestsPage}
        />
        <PermissionsRoute
          path="/(p-)?:partnerId?/pallets"
          exact
          component={PalletsPage}
          visibilitySettings="pallets"
          permissions="permissions.pallets.show"
          redirectTo="/"
        />
        <Route path="/terms-and-conditions" exact component={TermsAndConditions} />
        <Route component={DefaultPage} />
      </Switch>
      {process.env.REACT_APP_HUBSPOT_API ? <HubSpot /> : null}
    </MainLayout>
  );
}

const DefaultPage: React.FC = () => {
  const { refreshing } = useContext(PermissionsContext);
  const ordersShow = useHasPermissions('permissions.orders.show');

  const getMainPage = () => {
    return ordersShow ? '/orders' : null;
  };
  const redirect = getMainPage();
  return redirect ? refreshing ? null : <Redirect to={redirect} /> : null;
};
